import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { currentQuarter, quarterLabel } from "@/lib/periods";
import { Logo } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Star, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function Stars({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} className="transition-transform hover:scale-110" data-testid={`${testid}-${n}`}>
          <Star className={`h-8 w-8 ${n <= value ? "fill-accent text-accent" : "text-muted-foreground"}`} />
        </button>
      ))}
    </div>
  );
}

export default function SurveyForm() {
  const q = currentQuarter();
  const [type, setType] = useState("membro");
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [groups, setGroups] = useState([]);
  const [satisfaction, setSatisfaction] = useState(0);
  const [engagement, setEngagement] = useState(0);
  const [recommend, setRecommend] = useState(true);
  const [comments, setComments] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/groups", { params: { active_only: true } }).then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!satisfaction || !engagement) return toast.error("Avalie satisfação e engajamento.");
    try {
      await api.post("/surveys", {
        respondent_type: type, name, group_id: groupId || null,
        satisfaction, engagement, would_recommend: recommend, comments, quarter: q,
      });
      setDone(true);
    } catch {
      toast.error("Não foi possível enviar. Tente novamente.");
    }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <header className="border-b border-border bg-card/70 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo compact />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Início
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {done ? (
          <Card className="p-10 text-center animate-fade-up" data-testid="survey-thank-you">
            <CheckCircle2 className="h-14 w-14 text-[hsl(var(--emerald))] mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold">Obrigado pelo seu feedback!</h2>
            <p className="text-muted-foreground mt-2">Sua resposta ajuda a fortalecer os Pequenos Grupos de Discipulado.</p>
            <Link to="/"><Button className="mt-6">Voltar ao início</Button></Link>
          </Card>
        ) : (
          <Card className="p-8 animate-fade-up">
            <h1 className="font-display text-3xl font-bold tracking-tight">Pesquisa de Satisfação</h1>
            <p className="text-muted-foreground mt-1">{quarterLabel(q)} · Sua opinião é muito importante para nós.</p>

            <form onSubmit={submit} className="space-y-6 mt-8">
              <div>
                <Label>Você é</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="mt-1.5" data-testid="survey-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="membro">Participante / Membro</SelectItem>
                    <SelectItem value="lider">Líder de PGD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nome (opcional)</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="survey-name-input" />
                </div>
                <div>
                  <Label>Seu grupo (opcional)</Label>
                  <Select value={groupId} onValueChange={setGroupId}>
                    <SelectTrigger className="mt-1.5" data-testid="survey-group-select"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                    <SelectContent>
                      {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Qual seu nível de satisfação com o PGD?</Label>
                <div className="mt-2"><Stars value={satisfaction} onChange={setSatisfaction} testid="survey-satisfaction" /></div>
              </div>

              <div>
                <Label>Como avalia seu engajamento no grupo?</Label>
                <div className="mt-2"><Stars value={engagement} onChange={setEngagement} testid="survey-engagement" /></div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <Label className="cursor-pointer">Você recomendaria o PGD a um amigo?</Label>
                <Switch checked={recommend} onCheckedChange={setRecommend} data-testid="survey-recommend-switch" />
              </div>

              <div>
                <Label>Comentários e sugestões</Label>
                <Textarea value={comments} onChange={(e) => setComments(e.target.value)} className="mt-1.5" rows={4} data-testid="survey-comments-input" />
              </div>

              <Button type="submit" className="w-full" data-testid="survey-submit-button">Enviar feedback</Button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
